import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import type { ProblemDetailsWithCode } from "@/lib/problemDetails";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

type RematchResult = components["schemas"]["MatchRewindResult"];
type Population = components["schemas"]["RewindPopulation"];

// The generated schema widens int32 to `number | string`; counts are narrowed once here.
function toNumber(value: number | string | undefined): number {
  const parsed = typeof value === "string" ? Number(value) : value;
  return parsed === undefined || Number.isNaN(parsed) ? 0 : parsed;
}

// Date inputs are UTC calendar dates, as `new Date("2026-08-21")` parses them. The backend filters
// `CreatedAt <= to`, so createdTo needs endOfDayUtc or a same-day range selects nothing.
function startOfDayUtc(dateValue: string): string {
  return new Date(`${dateValue}T00:00:00.000Z`).toISOString();
}

function endOfDayUtc(dateValue: string): string {
  return new Date(`${dateValue}T23:59:59.999Z`).toISOString();
}

// ProblemDetails extensions are dynamic, so the generated type lacks `code` (see ProblemDetailsWithCode).

// Same population choice and wording as the clone dialog.
const POPULATIONS: { id: Population; title: string; blurb: string }[] = [
  {
    id: "AllBills",
    title: "All bills",
    blurb:
      "Every bill comes back for another pass, including ones the matcher never grouped because it " +
      "found no candidate. Those are the misses a settings change is most likely to fix, so this is " +
      "the default.",
  },
  {
    id: "GroupedOnly",
    title: "Only bills already matched",
    blurb:
      "Narrower: only bills the matcher already reached. Answers whether the matches you already make " +
      "get better, and cannot show you a miss being fixed.",
  },
];

interface RematchClientDialogProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RematchClientDialog({ clientId, open, onOpenChange }: RematchClientDialogProps) {
  const [population, setPopulation] = useState<Population>("AllBills");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RematchResult | null>(null);

  function resetForm() {
    setPopulation("AllBills");
    setCreatedFrom("");
    setCreatedTo("");
    setSubmitting(false);
    setError(null);
    setResult(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const { data, error: apiError } = await api.POST("/admin/clients/{clientId}/rematch", {
      params: { path: { clientId } },
      body: {
        population,
        createdFrom: createdFrom ? startOfDayUtc(createdFrom) : undefined,
        createdTo: createdTo ? endOfDayUtc(createdTo) : undefined,
      },
    });

    setSubmitting(false);

    if (apiError || !data) {
      // Never discard `detail`; code branches add guidance on top. Every failure here is pre-write, so unlike the
      // clone dialog there's no orphan case.
      const problem = apiError as ProblemDetailsWithCode | undefined;
      const detail = problem?.detail;
      let message: string;
      if (problem?.code === "Clone.NotAClone") {
        message = detail || "This client isn't a match-testing clone, so it can't be re-run.";
      } else if (problem?.code === "Clone.RewindScopeEmpty") {
        message =
          `${detail || "That window selects no bills."} Widen the created-from / created-to dates and ` +
          "try again.";
      } else if (problem?.code === "Clone.ScopeTooLarge") {
        message =
          `${detail || "That's too many bills for one synchronous run."} Narrow the created-from / ` +
          "created-to window and try again.";
      } else if (problem?.code === "Client.NotFound") {
        message = detail || "This client could not be found. It may have been removed.";
      } else {
        message = detail || "Failed to re-run matching.";
      }
      setError(message);
      toast.error("Re-run matching failed", { description: message });
      return;
    }

    setResult(data);
    toast.success("Matching re-run complete", {
      description:
        `${toNumber(data.billsRewound)} bills rewound, ${toNumber(data.matchGroupsCreated)} match ` +
        "groups created.",
    });
  }

  const contaminated = toNumber(result?.contaminatedPoLines);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>Matching re-run</DialogTitle>
              <DialogDescription>
                The clone's matches were dropped and the matcher ran again over the selected bills.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-7 gap-2 text-center text-sm">
              <Stat label="Bills" value={toNumber(result.billsRewound)} />
              <Stat label="Groups" value={toNumber(result.matchGroupsCreated)} />
              <Stat label="Exact" value={toNumber(result.exactAllocations)} />
              <Stat label="Fuzzy" value={toNumber(result.fuzzyAllocations)} />
              <Stat label="Auto-accepted" value={toNumber(result.autoAccepted)} />
              <Stat label="Unallocated" value={toNumber(result.linesUnallocated)} />
              <Stat label="Contaminated lines" value={contaminated} />
            </div>

            {/* Without this a constrained partial run looks exactly like a clean full one, and its counts aren't comparable. */}
            {contaminated > 0 && (
              <div className="border-amber-600/30 bg-amber-600/5 dark:border-amber-400/30 dark:bg-amber-400/5 space-y-1 rounded-md border p-3">
                <p className="text-amber-600 text-sm font-medium dark:text-amber-400">
                  This run is not comparable to a full run
                </p>
                <p className="text-amber-600 text-xs dark:text-amber-400">
                  {contaminated} order line{contaminated === 1 ? "" : "s"} referenced by bills in your
                  selection still carry matches from bills outside it. Those surviving matches keep
                  using up those lines' capacity, so this run had less room to match than a full run
                  would have had. Do not compare the counts above against a run over a different
                  selection, or judge a settings change on them alone - re-run over all bills first.
                </p>
              </div>
            )}

            <DialogFooter showCloseButton />
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Re-run matching</DialogTitle>
              <DialogDescription>
                Drops every match group, allocation, line resolution and match-line stat this clone
                holds, rewinds every selected bill to pending review, clears their approver lists, and
                reopens the purchase orders and delivery notes those matches were consuming. Then runs
                the matcher again from that clean slate.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label>Which bills</Label>
                {POPULATIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setPopulation(option.id)}
                    className={`w-full rounded-md border p-3 text-left transition ${
                      population === option.id ? "border-primary bg-accent" : "hover:bg-accent/50"
                    }`}
                  >
                    <div className="text-sm font-medium">{option.title}</div>
                    <p className="text-muted-foreground mt-1 text-xs">{option.blurb}</p>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="rematch-created-from">Created from</Label>
                  <Input
                    id="rematch-created-from"
                    type="date"
                    value={createdFrom}
                    onChange={(e) => setCreatedFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rematch-created-to">Created to</Label>
                  <Input
                    id="rematch-created-to"
                    type="date"
                    value={createdTo}
                    onChange={(e) => setCreatedTo(e.target.value)}
                  />
                </div>
              </div>
              <p className="text-muted-foreground text-xs">
                Leave both blank to rewind and re-run every bill on this clone. Dates are read as UTC
                calendar days, and created-to includes the whole of that day. Narrowing the window still
                leaves other bills' matches in place, which can leave the order lines your selection
                needs partly held by matches outside it - watch for the contamination warning after the
                run.
              </p>

              <div className="border-destructive/30 bg-destructive/5 space-y-1.5 rounded-md border p-3">
                <p className="text-destructive text-xs font-medium">There is no undo</p>
                <ul className="text-muted-foreground list-inside list-disc space-y-1 text-xs">
                  <li>
                    Every match group, allocation, line resolution and match-line stat in scope is
                    deleted before the matcher runs again.
                  </li>
                  <li>
                    Any review decision recorded against those matches is gone. Re-running the matcher
                    produces the engine's opinion, not the decision a person made - it does not restore
                    what was there.
                  </li>
                  <li>
                    Selected bills return to pending review, their approver lists are cleared, and the
                    purchase orders and delivery notes those matches were consuming reopen.
                  </li>
                  <li>This is only safe here because this client is a clone made for exactly this.</li>
                </ul>
              </div>

              {error && <p className="text-destructive text-sm">{error}</p>}
            </div>

            <DialogFooter className="mt-6">
              <Button type="submit" variant="destructive" disabled={submitting}>
                {submitting && <Spinner className="size-4" />}
                Re-run matching
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-lg font-semibold">{value.toLocaleString()}</div>
      <div className="text-muted-foreground text-xs">{label}</div>
    </div>
  );
}
